import { recordHttpFailure } from '@/composables/useConsoleLogBuffer'
import router, { navigationState } from '@/router'
import pinia from '@/stores'
import { useCompanyBlockStore } from '@/stores/companyBlock'
import Axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'

const axios = Axios.create({
	baseURL: import.meta.env.VITE_PUBLIC_BACKEND_URL,
	headers: {
		'X-Requested-With': 'XMLHttpRequest',
	},
	withCredentials: true,
	withXSRFToken: true,
	// Cota dura para que un backend/Sabre colgado NO deje girando el spinner de
	// reserva para siempre (antes: sin timeout = infinito). Holgado para llamadas
	// lentas legítimas de Sabre (search/price/book), pero acotado para que el
	// flujo SIEMPRE resuelva a éxito o a un error accionable.
	timeout: 60000,
})

// Inicio de cada petición, para medir duración en el log de diagnóstico. WeakMap
// en lugar de `config.metadata` para no aumentar los tipos de axios.
const requestStart = new WeakMap<InternalAxiosRequestConfig, number>()

axios.interceptors.request.use((config) => {
	requestStart.set(config, performance.now())

	return config
})

/** Path sin query string: el query puede traer datos personales. */
function safePath(url?: string, baseURL?: string): string {
	if (!url) return '(desconocido)'

	try {
		return new URL(url, baseURL || window.location.origin).pathname
	} catch {
		return url.split('?')[0]
	}
}

/** Mensaje del backend, o el código de axios cuando no hubo respuesta. */
function failureMessage(error: any): string | undefined {
	const data = error.response?.data

	if (typeof data === 'string') return data
	if (data?.message) return String(data.message)
	if (error.code) return String(error.code)

	return error.message ? String(error.message) : undefined
}

axios.interceptors.response.use(null, (error) => {
	// Toda petición fallida queda en el buffer que viaja como `error_log` del
	// reporte de bug. Va antes de cualquier return para que también se registren
	// los 423 de empresa bloqueada y los redirects a /error.
	const config = error.config
	const start = config ? requestStart.get(config) : undefined

	recordHttpFailure({
		method: config?.method,
		path: safePath(config?.url, config?.baseURL),
		status: error.response?.status ?? 0,
		durationMs: start !== undefined ? performance.now() - start : undefined,
		message: failureMessage(error),
	})

	// Empresa suspendida/bloqueada por el backoffice: BookingV2 (middleware
	// company.active) responde 423 con { companyBlocked, status, reason } en
	// CUALQUIER ruta protegida del cliente. Capturamos el motivo y mandamos a la
	// pantalla de suspensión. El estado es reversible: al resolverse, /api/user
	// vuelve a 200 y useAuth limpia el store.
	//
	// El `replace` de aquí es solo para el 423 que llega FUERA de una navegación
	// (una query en background en una pantalla ya cargada): con navegación en
	// curso, `router.currentRoute` todavía apunta a la ruta anterior mientras el
	// guard espera este mismo `refetchAuth()`, así que el replace competía con el
	// del guard (ver su comentario en `router/index.ts`) y ninguno llegaba a
	// asentar — loop de replaces pegándole a /api/user sin fin.
	const data = error.response?.data
	if (error.response?.status === 423 && data?.companyBlocked) {
		useCompanyBlockStore(pinia).set({
			status: data.status,
			reason: data.reason,
		})

		if (!navigationState.pending && router.currentRoute.value.name !== 'company-suspended') {
			router.replace({ name: 'company-suspended' })
		}

		return Promise.reject(error)
	}

	let errorPath = '/error'
	switch (error.response?.status) {
		case 404:
			errorPath += '/not-found'
			break
		case 403:
			errorPath += '/forbidden'
			break
		// case 401:
		//   errorPath += "/not-found";
		//   break;
		// x
	}

	// Verificar si se debe redirigir
	if (error.config?.errorHandling !== false && errorPath.match(/\/error\/.*/)) {
		router.replace({ path: errorPath })
	}

	return Promise.reject(error)
})

export default axios
